import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { MapPin, ArrowLeft, Crown, Star, ArrowRight, CaretLeft, CaretRight } from "@phosphor-icons/react";

const PAGE_SIZE = 30;

export default function NearbyPage() {
  const [params] = useSearchParams();
  const lat = parseFloat(params.get("lat"));
  const lon = parseFloat(params.get("lon"));
  const [radius, setRadius] = useState(10);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lat || !lon) return;
    setLoading(true);
    const offset = page * PAGE_SIZE;
    Promise.all([
      api.get(`/public/nearby?lat=${lat}&lon=${lon}&radius_km=${radius}&limit=${PAGE_SIZE}&offset=${offset}`).then((r) => r.data),
      page === 0 ? api.get(`/public/nearest-city?lat=${lat}&lon=${lon}`).then((r) => r.data) : Promise.resolve(city),
    ])
      .then(([nb, nc]) => { setData(nb); if (page === 0) setCity(nc); window.scrollTo({ top: 0, behavior: "smooth" }); })
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  }, [lat, lon, radius, page]);

  // Reset to page 0 when radius changes
  useEffect(() => { setPage(0); }, [radius]);

  if (isNaN(lat) || isNaN(lon)) {
    return (
      <div className="mx-auto max-w-2xl p-12 text-center" data-testid="nearby-missing">
        <p className="mb-4">Position manquante — utilisez le bouton « Autour de moi » depuis la page d'accueil.</p>
        <Link to="/" className="text-primary underline">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="nearby-page">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14}/> Accueil
      </Link>

      <div className="mb-10">
        <div className="label-uppercase mb-3 flex items-center gap-2"><MapPin size={12} weight="fill"/> Autour de vous</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-3">
          Commerces près de vous
        </h1>
        {city?.city && (
          <p className="text-muted-foreground">
            Ville la plus proche : <Link to={`/villes/${slug(city.city)}`} className="font-semibold text-foreground hover:text-primary">{city.city}</Link>
            {city.distance_km != null && <> · à <b>{city.distance_km} km</b></>}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 mb-8">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Rayon :</span>
        {[5, 10, 20, 50].map((r) => (
          <button
            key={r}
            onClick={() => setRadius(r)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${r === radius ? "bg-primary text-primary-foreground" : "border border-border bg-card hover:border-primary"}`}
            data-testid={`nearby-radius-${r}`}
          >
            {r} km
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Chargement…</div>
      ) : !data || data === false ? (
        <div className="text-muted-foreground">Erreur de chargement</div>
      ) : data.items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-sm p-12 text-center" data-testid="nearby-empty">
          Aucun commerce dans un rayon de {radius} km. Essayez d'élargir le rayon.
        </div>
      ) : (
        <div>
          <div className="text-sm text-muted-foreground mb-4" data-testid="nearby-count">
            {data.total} résultat{data.total > 1 ? "s" : ""} trouvé{data.total > 1 ? "s" : ""}
            {data.total > PAGE_SIZE && (
              <> · Page <b>{page + 1}</b> sur <b>{Math.ceil(data.total / PAGE_SIZE)}</b></>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="nearby-grid">
            {data.items.map((b) => (
              <Link
                key={b.id}
                to={`/entreprise/${b.slug}`}
                className="border border-border rounded-sm p-5 bg-card lift-card-plus block"
                data-testid={`nearby-card-${b.slug}`}
              >
                <div className="flex items-center justify-between mb-2">
                  {b.premium?.tier && b.premium.tier !== "free" && (
                    <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm">
                      <Crown size={10} weight="fill"/> Premium
                    </span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">{b.distance_km} km</span>
                </div>
                <div className="font-display text-lg font-medium mb-1">{b.name}</div>
                <div className="text-xs text-muted-foreground mb-3">{b.category} · {b.city}</div>
                {b.rating_count > 0 && (
                  <div className="flex items-center gap-1 text-sm text-secondary mb-2">
                    <Star size={14} weight="fill"/>
                    <span className="font-semibold">{b.rating_avg?.toFixed(1)}</span>
                    <span className="text-muted-foreground">· {b.rating_count} avis</span>
                  </div>
                )}
                <div className="text-xs text-primary inline-flex items-center gap-1 font-semibold mt-2">
                  Voir la fiche <ArrowRight size={12}/>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination — appears when there are more results than one page */}
          {data.total > PAGE_SIZE && (
            <nav className="mt-10 flex items-center justify-center gap-2 flex-wrap" data-testid="nearby-pagination" aria-label="Pagination">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="inline-flex items-center gap-1.5 border border-border rounded-sm px-3 py-2 text-xs font-semibold bg-card hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="nearby-page-prev"
              >
                <CaretLeft size={12} weight="bold"/> Précédent
              </button>
              {(() => {
                const total = Math.ceil(data.total / PAGE_SIZE);
                const nums = [];
                // Show first, last, current ±2 with ellipses
                const push = (n) => nums.push(n);
                for (let i = 0; i < total; i++) {
                  if (i === 0 || i === total - 1 || Math.abs(i - page) <= 2) push(i);
                  else if (nums[nums.length - 1] !== "…") push("…");
                }
                return nums.map((n, i) => n === "…" ? (
                  <span key={`e${i}`} className="px-2 text-muted-foreground select-none">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    disabled={loading}
                    className={`min-w-[36px] px-2 py-2 text-xs font-semibold rounded-sm ${n === page ? "bg-primary text-primary-foreground" : "border border-border bg-card hover:border-primary"}`}
                    data-testid={`nearby-page-${n + 1}`}
                    aria-current={n === page ? "page" : undefined}
                  >
                    {n + 1}
                  </button>
                ));
              })()}
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(data.total / PAGE_SIZE) - 1 || loading}
                className="inline-flex items-center gap-1.5 border border-border rounded-sm px-3 py-2 text-xs font-semibold bg-card hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="nearby-page-next"
              >
                Suivant <CaretRight size={12} weight="bold"/>
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}

function slug(s) {
  return (s || "").toString().toLowerCase().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
